import React, { useEffect, useState, useMemo } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonLoading,
} from "@ionic/react";
import {
  Box,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Typography,
  TablePagination,
  InputAdornment,
  Card,
  CardContent,
  Avatar,
  Grid,
  TableSortLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Tooltip,
  Divider,
  TableFooter,
} from "@mui/material";
import { Search, Person, CalendarToday, Close, Visibility, Phone, KeyboardArrowDown, AccessTime, TrendingUp } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getStaffPerformance, getStaffTransactionReport } from "../../../api/customers/customers";
import { MenuButton } from "../../../components/Menu";

interface StaffPerformance {
  id: number;
  business_id: string;
  branch_id: string;
  user_id: string;
  entity_id: string;
  roleID: number;
  status: number;
  email: string;
  phone: string;
  position: string;
  last_updated: string;
  firstName: string;
  lastName: string;
  altEmail: string;
  totalTransactions: number;
  totalCustomers: number;
  totalSellingPrice: number;
  totalCostPrice: number;
  totalActualCostPrice: number;
  totalCommission: number;
  totalActualCommission: number;
  totalmins: number;
}

interface TransactionDetail {
  id: number;
  active_status: string;
  amount_paid: string;
  balance_due: string;
  branch_id: string;
  business_id: string;
  commission: string;
  costPrice: string;
  created_at: string;
  customer_account_name: string;
  customer_account_number: string;
  customer_bank_code: string;
  customer_email: string;
  customer_name: string;
  customer_phone: string;
  email: string;
  firstName: string;
  lastName: string;
  is_audited: number;
  is_confirmed: number;
  is_verified: number;
  load_type: string;
  member_id: string;
  mode_of_payment: string;
  naira_rate: string;
  new_commission: string;
  new_costPrice: string;
  order_by: string;
  order_code: string;
  order_date: string;
  order_id: string;
  payable_amount: string;
  payment_status: number;
  payout_date: string;
  payout_status: number;
  phone: string;
  processor_category: string;
  processor_type: string;
  product_face_code: string;
  product_id_single: string;
  product_id_type: string;
  request_id: string;
  rmbValue: string;
  roleID: number;
  sellingPrice: string;
  total_price: string;
  transactionStartTime: string;
  transactionEndTime: string;
  updated_at: string | null;
  user_id: string;
  username: string;
  external_processor?: string;
  staff_name?: string;
  giftcardType?: string;
  cryptoType?: string;
}

type Order = 'asc' | 'desc';

interface HeadCell {
  id: keyof StaffPerformance | 'actions';
  label: string;
  numeric: boolean;
  sortable: boolean;
}

const headCells: HeadCell[] = [
  { id: 'firstName', label: 'Staff Member', numeric: false, sortable: true },
  { id: 'totalTransactions', label: 'Trans', numeric: true, sortable: true },
  { id: 'totalCustomers', label: 'Cust', numeric: true, sortable: true },
  { id: 'totalmins', label: 'Avg. Resp', numeric: true, sortable: true },
  { id: 'totalSellingPrice', label: 'Selling Price', numeric: true, sortable: true },
  { id: 'totalCostPrice', label: 'Cost Price', numeric: true, sortable: true },
  { id: 'totalActualCostPrice', label: 'Actual CP', numeric: true, sortable: true },
  { id: 'totalCommission', label: 'Comm', numeric: true, sortable: true },
  { id: 'totalActualCommission', label: 'Actual Comm', numeric: true, sortable: true },
  { id: 'actions', label: 'Actions', numeric: false, sortable: false },
];

const StaffPerformancePage1: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const [staffPerformance, setStaffPerformance] = useState<StaffPerformance[]>([]);
  const [filteredData, setFilteredData] = useState<StaffPerformance[]>([]);
  const [status, setStatus] = useState({ loading: false, error: false });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null,
  });
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof StaffPerformance>('totalTransactions');

  const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<StaffPerformance | null>(null);
  const [transactionDetails, setTransactionDetails] = useState<TransactionDetail[]>([]);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownDateRange, setBreakdownDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null,
  });

  const fetchStaffPerformance = async (startDate?: Date | null, endDate?: Date | null) => {
    setStatus({ loading: true, error: false });
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate.toISOString().split('T')[0];
      if (endDate) params.endDate = endDate.toISOString().split('T')[0];
      
      const response = await getStaffPerformance(params);
      const staffData = response.data || response.data?.data || [];
      setStaffPerformance(staffData);
      setFilteredData(staffData);
    } catch (error) {
      console.error("Error fetching staff performance:", error);
      setStatus({ loading: false, error: true });
    } finally {
      setStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchStaffTransactionReport = async (staffId: string, startDate?: Date | null, endDate?: Date | null) => {
    setBreakdownLoading(true);
    try {
      const params: any = { staffId };
      if (startDate) params.startDate = startDate.toISOString().split('T')[0];
      if (endDate) params.endDate = endDate.toISOString().split('T')[0];
      
      const response = await getStaffTransactionReport(params);
      const transactionData = response.data || response.data?.data || [];
      setTransactionDetails(transactionData);
    } catch (error) {
      console.error("Error fetching staff transaction report:", error);
      setTransactionDetails([]);
    } finally {
      setBreakdownLoading(false);
    }
  };

  const handleViewBreakdown = async (staff: StaffPerformance) => {
    setSelectedStaff(staff);
    setBreakdownDialogOpen(true);
    setBreakdownDateRange({ start: null, end: null });
    await fetchStaffTransactionReport(staff.user_id);
  };

  const handleBreakdownDateRangeChange = (field: 'start' | 'end') => async (date: Date | null) => {
    const newDateRange = {
      ...breakdownDateRange,
      [field]: date
    };
    setBreakdownDateRange(newDateRange);

    if (selectedStaff && newDateRange.start && newDateRange.end) {
      await fetchStaffTransactionReport(selectedStaff.user_id, newDateRange.start, newDateRange.end);
    } else if (selectedStaff && !newDateRange.start && !newDateRange.end) {
      await fetchStaffTransactionReport(selectedStaff.user_id);
    }
  };

  const handleCloseBreakdownDialog = () => {
    setBreakdownDialogOpen(false);
    setSelectedStaff(null);
    setTransactionDetails([]);
    setBreakdownDateRange({ start: null, end: null });
  };

  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      fetchStaffPerformance(dateRange.start, dateRange.end);
    } else if (!dateRange.start && !dateRange.end) {
      fetchStaffPerformance();
    }
  }, [dateRange]);

  useEffect(() => {
    let filtered = staffPerformance;

    if (searchQuery) {
      filtered = filtered.filter((staff) => {
        const fullName = `${staff.firstName || ""} ${staff.lastName || ""}`.toLowerCase();
        const phone = (staff.phone || "").toLowerCase();
        const email = (staff.email || "").toLowerCase();
        const position = (staff.position || "").toLowerCase();
        const query = searchQuery.toLowerCase();

        return (
          fullName.includes(query) ||
          phone.includes(query) ||
          email.includes(query) ||
          position.includes(query)
        );
      });
    }

    setFilteredData(filtered);
    setPage(0);
  }, [searchQuery, staffPerformance]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleDateRangeChange = (field: 'start' | 'end') => (date: Date | null) => {
    setDateRange(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleRequestSort = (property: keyof StaffPerformance) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-NG", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch (error) {
      return "N/A";
    }
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) || 0 : amount;
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(numAmount);
  };

  const getDuration = (start: string, end: string) => {
    if (!start || !end) return "0 mins";
    const startTime = new Date(start);
    const endTime = new Date(end);

    if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) return "0 mins";

    const diffMs = endTime.getTime() - startTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    return `${diffMins >= 0 ? diffMins : 0} mins`;
  };

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      let aValue: any = a[orderBy];
      let bValue: any = b[orderBy];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (orderBy === 'last_updated') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, order, orderBy]);

  const tableTotals = {
    totalTransactions: filteredData.reduce((sum, staff) => sum + (staff.totalTransactions || 0), 0),
    totalCustomers: filteredData.reduce((sum, staff) => sum + (staff.totalCustomers || 0), 0),
    totalSellingPrice: filteredData.reduce((sum, staff) => sum + (staff.totalSellingPrice || 0), 0),
    totalCostPrice: filteredData.reduce((sum, staff) => sum + (staff.totalCostPrice || 0), 0),
    totalActualCostPrice: filteredData.reduce((sum, staff) => sum + (staff.totalActualCostPrice || 0), 0),
    totalCommission: filteredData.reduce((sum, staff) => sum + (staff.totalCommission || 0), 0),
    totalActualCommission: filteredData.reduce((sum, staff) => sum + (staff.totalActualCommission || 0), 0),
  };

  const breakdownTotals = transactionDetails.reduce(
    (acc, tx) => ({
      totalAmountPaid: acc.totalAmountPaid + (parseFloat(tx.amount_paid) || 0),
      totalCommission: acc.totalCommission + (parseFloat(tx.commission) || 0),
      totalActualCommission: acc.totalActualCommission + (parseFloat(tx.new_commission) || 0),
      totalSellingPrice: acc.totalSellingPrice + (parseFloat(tx.sellingPrice) || 0),
      totalCostPrice: acc.totalCostPrice + (parseFloat(tx.costPrice) || 0),
      totalActualCostPrice: acc.totalActualCostPrice + (parseFloat(tx.new_costPrice) || 0),
      totalTransactions: acc.totalTransactions + 1,
    }),
    { 
      totalAmountPaid: 0, 
      totalCommission: 0, 
      totalActualCommission: 0,
      totalSellingPrice: 0, 
      totalCostPrice: 0, 
      totalActualCostPrice: 0,
      totalTransactions: 0 
    }
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <IonPage className="pushDown">
        <IonHeader>
          <IonToolbar>
            <MenuButton />
            <IonTitle>Staff Performance</IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding">
          <IonLoading
            isOpen={status.loading}
            message={"Fetching performance data..."}
            spinner="circles"
          />

          <Box sx={{ mb: 4 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                  Staff Metrics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Analysis of staff activities and response times
                </Typography>
              </Box>
              <Button variant="outlined" startIcon={<Search />} onClick={() => fetchStaffPerformance(dateRange.start, dateRange.end)} sx={{ borderRadius: 2 }}>
                Refresh
              </Button>
            </Stack>

            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <DatePicker
                        label="Start Date"
                        value={dateRange.start}
                        onChange={handleDateRangeChange('start')}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                      <DatePicker
                        label="End Date"
                        value={dateRange.end}
                        onChange={handleDateRangeChange('end')}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search staff members..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><Search color="primary" /></InputAdornment>,
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          {/* Desktop Table View */}
          <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    {headCells.map((headCell) => (
                      <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        sx={{ fontWeight: 'bold', py: 2 }}
                        sortDirection={orderBy === headCell.id ? order : false}
                      >
                        {headCell.sortable ? (
                          <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={() => handleRequestSort(headCell.id as keyof StaffPerformance)}
                          >
                            {headCell.label}
                          </TableSortLabel>
                        ) : headCell.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((staff) => (
                    <TableRow key={staff.user_id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar sx={{ bgcolor: 'primary.light', fontWeight: 'bold' }}>{staff.firstName?.charAt(0)}</Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{`${staff.firstName} ${staff.lastName}`}</Typography>
                            <Typography variant="caption" color="text.secondary">{staff.position || "Staff"}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{staff.totalTransactions}</TableCell>
                      <TableCell align="right">{staff.totalCustomers}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {staff.totalTransactions ? (staff.totalmins / staff.totalTransactions).toFixed(1) : 0}m
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'medium' }}>{formatCurrency(staff.totalSellingPrice)}</TableCell>
                      <TableCell align="right">{formatCurrency(staff.totalCostPrice)}</TableCell>
                      <TableCell align="right" sx={{ color: 'warning.main' }}>{formatCurrency(staff.totalActualCostPrice)}</TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 'medium' }}>{formatCurrency(staff.totalCommission)}</TableCell>
                      <TableCell align="right" sx={{ color: 'secondary.main', fontWeight: 'medium' }}>{formatCurrency(staff.totalActualCommission)}</TableCell>
                      <TableCell>
                        <Button variant="outlined" size="small" startIcon={<Visibility />} onClick={() => handleViewBreakdown(staff)} sx={{ borderRadius: 1.5, textTransform: 'none' }}>
                          Breakdown
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter sx={{ bgcolor: 'grey.100' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>TOTALS</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{tableTotals.totalTransactions}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{tableTotals.totalCustomers}</TableCell>
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(tableTotals.totalSellingPrice)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(tableTotals.totalCostPrice)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(tableTotals.totalActualCostPrice)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(tableTotals.totalCommission)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(tableTotals.totalActualCommission)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
              <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
              />
            </TableContainer>
          </Box>

          {/* Mobile Card List View */}
          <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
            {/* Mobile Summary Card */}
            <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>OVERALL TOTALS ({filteredData.length} Staff)</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Volume</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{formatCurrency(tableTotals.totalSellingPrice)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Commission</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{formatCurrency(tableTotals.totalCommission)}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((staff) => (
              <Card key={staff.user_id} sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.light', fontWeight: 'bold', width: 48, height: 48 }}>{staff.firstName?.charAt(0)}</Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{`${staff.firstName} ${staff.lastName}`}</Typography>
                      <Typography variant="caption" color="text.secondary">{staff.position || "Staff Member"}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{staff.totalTransactions}</Typography>
                      <Typography variant="caption" color="text.secondary">Trans.</Typography>
                    </Box>
                  </Stack>

                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Box sx={{ p: 1, bgcolor: 'success.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Sales Volume</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatCurrency(staff.totalSellingPrice)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ p: 1, bgcolor: 'info.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Avg Response</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {staff.totalTransactions ? (staff.totalmins / staff.totalTransactions).toFixed(1) : 0} mins
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ p: 1, bgcolor: 'primary.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Commission</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(staff.totalCommission)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ p: 1, bgcolor: 'secondary.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Actual Comm</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>{formatCurrency(staff.totalActualCommission)}</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ mb: 1.5 }} />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{staff.phone || "N/A"}</Typography>
                    </Stack>
                    <Button variant="contained" size="small" startIcon={<Visibility />} onClick={() => handleViewBreakdown(staff)} sx={{ borderRadius: 1.5, textTransform: 'none' }}>
                      View Breakdown
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </Box>

          {/* Breakdown Dialog */}
          <Dialog
            open={breakdownDialogOpen}
            onClose={handleCloseBreakdownDialog}
            fullScreen={isMobile}
            maxWidth="xl"
            fullWidth
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
          >
            <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', py: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6">Performance Breakdown</Typography>
                  <Typography variant="caption">{selectedStaff?.firstName} {selectedStaff?.lastName}</Typography>
                </Box>
                <IconButton onClick={handleCloseBreakdownDialog} sx={{ color: 'white' }}><Close /></IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ p: { xs: 1, md: 3 } }}>
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CalendarToday sx={{ color: 'text.secondary' }} />
                      <DatePicker
                        label="From"
                        value={breakdownDateRange.start}
                        onChange={handleBreakdownDateRangeChange('start')}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                      <DatePicker
                        label="To"
                        value={breakdownDateRange.end}
                        onChange={handleBreakdownDateRangeChange('end')}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              {breakdownLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
              ) : (
                <>
                  {/* Breakdown Summary Stats */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">Transactions</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{breakdownTotals.totalTransactions}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">Total Value</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{formatCurrency(breakdownTotals.totalSellingPrice)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">Comm.</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(breakdownTotals.totalCommission)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">Actual Comm.</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>{formatCurrency(breakdownTotals.totalActualCommission)}</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Desktop Table for Breakdown */}
                  {!isMobile && (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell>Order Code</TableCell>
                            <TableCell>Product</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">Comm.</TableCell>
                            <TableCell align="right">Actual Comm.</TableCell>
                            <TableCell align="center">Duration</TableCell>
                            <TableCell>Processor</TableCell>
                            <TableCell>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {transactionDetails.map((tx) => (
                            <TableRow key={tx.id} hover>
                              <TableCell sx={{ fontWeight: 'bold' }}>{tx.order_code}</TableCell>
                              <TableCell>
                                <Typography variant="body2">{tx.product_id_type}</Typography>
                                <Typography variant="caption" color="text.secondary">{tx.giftcardType || tx.cryptoType}</Typography>
                              </TableCell>
                              <TableCell align="right">{formatCurrency(tx.amount_paid)}</TableCell>
                              <TableCell align="right" sx={{ color: 'primary.main' }}>{formatCurrency(tx.commission)}</TableCell>
                              <TableCell align="right" sx={{ color: 'secondary.main' }}>{formatCurrency(tx.new_commission)}</TableCell>
                              <TableCell align="center">
                                <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                  <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption">{getDuration(tx.transactionStartTime, tx.transactionEndTime)}</Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" display="block">{tx.external_processor || "N/A"}</Typography>
                                <Chip label={tx.load_type} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                              </TableCell>
                              <TableCell>{formatDate(tx.order_date)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* Mobile Cards for Breakdown */}
                  {isMobile && (
                    <Stack spacing={2}>
                      {transactionDetails.map((tx) => (
                        <Card key={tx.id} variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardContent sx={{ p: 2 }}>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{tx.order_code}</Typography>
                              <Typography variant="caption" color="text.secondary">{formatDate(tx.order_date)}</Typography>
                            </Stack>
                            
                            <Grid container spacing={1} sx={{ mb: 1.5 }}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">Product</Typography>
                                <Typography variant="body2">{tx.product_id_type} ({tx.giftcardType || tx.cryptoType})</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">Duration</Typography>
                                <Typography variant="body2">{getDuration(tx.transactionStartTime, tx.transactionEndTime)}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">Value</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatCurrency(tx.amount_paid)}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">Comm.</Typography>
                                <Typography variant="body2" color="primary.main">{formatCurrency(tx.commission)}</Typography>
                              </Grid>
                            </Grid>
                            
                            <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                            
                            <Typography variant="caption" color="text.secondary">
                              {tx.external_processor || "N/A"} • {tx.load_type}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseBreakdownDialog} variant="contained" fullWidth={isMobile}>Close</Button>
            </DialogActions>
          </Dialog>
        </IonContent>
      </IonPage>
    </LocalizationProvider>
  );
};

export default StaffPerformancePage1;
